import React from 'react';

const PLVProposalTemplate: React.FC = () => {
  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8 text-center border-b border-gray-300 pb-6">
        <h1 className="text-3xl font-bold text-blue-900 mb-2">DR. PETER LE VOI x NEXT GENERATION MEDICINE</h1>
        <p className="text-xl text-gray-600 italic">Proposal for Strategic Advisory and Practice Development</p>
      </div>

      {/* Overview Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-blue-900 mb-4">Overview</h2>
        <p className="text-gray-800 mb-4">
          Dr. Le Voi is building something rare: a forward-looking concierge practice rooted in clinical rigor and informed by the evolving science of longevity. With a strong foundation and an entrepreneurial mindset, the practice is well-positioned to stand out in a growing but undifferentiated field.
        </p>
        <p className="text-gray-800 mb-4">
          The opportunity now is to clarify the model, build scalable systems, and shape a compelling value proposition—one that supports patient outcomes, operational efficiency, and long-term sustainability.
        </p>
      </div>

      {/* What I Bring Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-blue-900 mb-4">What I Bring</h2>
        <p className="text-gray-800 mb-4">
          Over the past decade, I've built and scaled multiple businesses at the intersection of medicine and innovation—including as Chief Medical Officer at Modern Age, Head of Longevity Medicine at Midi Health, and advisor to Superpower.
        </p>
        <p className="text-gray-800 mb-4">My expertise integrates:</p>
        <ul className="list-disc pl-6 mb-4 text-gray-800">
          <li className="mb-2">Clinical program and protocol design</li>
          <li className="mb-2">Strategic operations and business model architecture</li>
          <li className="mb-2">Tools for scalable training, diagnostics, and performance tracking</li>
        </ul>
        <p className="text-gray-800 mb-4">
          The result: practical execution with a long-term strategic lens. I work directly with founders and clinicians to translate ambition into a working model that grows.
        </p>
      </div>

      {/* Strategic Focus Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-blue-900 mb-4">Strategic Focus for Your Practice</h2>
        
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-blue-800 mb-2">1. Program and Protocol Design</h3>
          <ul className="list-disc pl-6 mb-4 text-gray-800">
            <li className="mb-2">Define service tiers (e.g., assessments, memberships, performance pathways)</li>
            <li className="mb-2">Build protocols that balance clinical depth with efficiency and scalability</li>
            <li className="mb-2">Align offerings with your vision while positioning for clear patient outcomes</li>
          </ul>
        </div>
        
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-blue-800 mb-2">2. Market Strategy & Messaging</h3>
          <ul className="list-disc pl-6 mb-4 text-gray-800">
            <li className="mb-2">Clarify your positioning in the local and regional ecosystem</li>
            <li className="mb-2">Identify patient archetypes and tailor content/messaging accordingly</li>
            <li className="mb-2">Ensure strategic alignment between services, brand, and go-to-market efforts</li>
          </ul>
        </div>
        
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-blue-800 mb-2">3. Operational Enablement</h3>
          <ul className="list-disc pl-6 mb-4 text-gray-800">
            <li className="mb-2">Architect the patient funnel: from intake to conversion to ongoing membership</li>
            <li className="mb-2">Implement automation and tools (e.g., AI-powered diagnostics) to reduce friction</li>
            <li className="mb-2">Lay the groundwork for clinician onboarding and future expansion</li>
          </ul>
        </div>
        
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-blue-800 mb-2">4. Performance Feedback & Analytics</h3>
          <ul className="list-disc pl-6 mb-4 text-gray-800">
            <li className="mb-2">Track patient concerns, visit patterns, and membership transitions</li>
            <li className="mb-2">Identify conversion points and drop-offs within your practice funnel</li>
            <li className="mb-2">Build a dashboard-driven feedback loop to inform strategic adjustments</li>
          </ul>
        </div>
      </div>

      {/* Engagement Models Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-blue-900 mb-4">Preferred Engagement Model: Asynchronous Advisory</h2>
        <p className="text-gray-800 mb-4">
          This is the model I recommend based on your current stage and goals.
        </p>
        <ul className="list-disc pl-6 mb-4 text-gray-800">
          <li className="mb-2">Slack-based collaboration with 24-hour response time</li>
          <li className="mb-2">Feedback on strategy, clinical materials, and business model evolution</li>
          <li className="mb-2">Check-ins and video calls as needed</li>
          <li className="mb-2">Fee: $2,500/month</li>
        </ul>
        <p className="text-gray-800 mb-4">
          This model enables fast, iterative progress without unnecessary overhead, offering continuous access to strategic input and tools.
        </p>
      </div>

      {/* Alternative Option */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-blue-900 mb-4">Other Option: Mentorship Program</h2>
        <ul className="list-disc pl-6 mb-4 text-gray-800">
          <li className="mb-2">Annual curriculum covering clinical science, business strategy, and tech integration</li>
          <li className="mb-2">Monthly group Q&A sessions</li>
          <li className="mb-2">Peer community of clinicians and operators</li>
          <li className="mb-2">Fee: $5,000/year</li>
        </ul>
      </div>

      {/* Why This Works Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-blue-900 mb-4">Why This Works</h2>
        <p className="text-gray-800 mb-4">
          You're not just launching a clinic—you're crafting a model of care that bridges longevity science with high-trust, relationship-based medicine. That demands more than good intentions; it requires clarity, systems, and the discipline to avoid common early-stage traps.
        </p>
        <p className="text-gray-800 mb-4">
          My role is to help you build exactly that—efficiently and intelligently.
        </p>
      </div>

      {/* Next Steps Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-blue-900 mb-4">Next Steps</h2>
        <p className="text-gray-800 mb-6">
          If this direction resonates, I'd be glad to send over sample materials—protocol frameworks, diagnostic tools, and content guides. Let's continue the conversation and design a model that sets your practice apart.
        </p>
      </div>

      {/* Footer */}
      <div className="mt-12 pt-6 border-t border-gray-300 text-center">
        <p className="text-gray-800 font-semibold">Anant Vinjamoori, MD MBA</p>
        <p className="text-gray-800">Founder & President</p>
        <p className="text-gray-800">Next Generation Medicine</p>
        <a href="https://www.nextgenerationmedicine.co" className="text-blue-800 hover:underline" target="_blank" rel="noopener noreferrer">
          www.nextgenerationmedicine.co
        </a>
      </div>
    </div>
  );
};

export default PLVProposalTemplate;